import React, { useMemo, useState } from 'react';
import './CountrySelector.css';

// List of countries
const COUNTRIES = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", 
    "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", 
    "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", 
    "Belarus", "Belgium", "Belize", "Benin", "Bhutan", 
    "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", 
    "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", 
    "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile", 
    "China", "Colombia", "Comoros", "Congo, Democratic Republic of the", 
    "Congo, Republic of the", "Costa Rica", "Croatia", "Cuba", "Cyprus", 
    "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", 
    "East Timor", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", 
    "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", 
    "Finland", "France", "Gabon", "Gambia", "Georgia", 
    "Germany", "Ghana", "Greece", "Grenada", "Guatemala", 
    "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", 
    "Hungary", "Iceland", "India", "Indonesia", "Iran", 
    "Iraq", "Ireland", "Israel", "Italy", "Jamaica", 
    "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", 
    "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", 
    "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", 
    "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", 
    "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", 
    "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", 
    "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", 
    "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", 
    "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", 
    "Oman", "Pakistan", "Palau", "Palestine", "Panama", 
    "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", 
    "Portugal", "Qatar", "Romania", "Russia", "Rwanda", 
    "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", 
    "Samoa", "San Marino", "São Tomé and Príncipe", "Saudi Arabia", 
    "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", 
    "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", 
    "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", 
    "Suriname", "Sweden", "Switzerland", "Syria", "Tajikistan", 
    "Tanzania", "Thailand", "Togo", "Tonga", "Trinidad and Tobago", 
    "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", 
    "Ukraine", "United Arab Emirates", "United Kingdom", "United States", 
    "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", 
    "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

const CountrySelector = ({
  value,
  onChange,
  className,
  placeholder = "Select Country",
  disabled = false,
  required = true,
  name = "country",
  id,
  label,
  error,
  searchable = true
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Create a sorted copy of countries once for performance
  const sortedCountries = useMemo(() => [...COUNTRIES].sort(), []);

  // Filter countries based on search term
  const filteredCountries = useMemo(() => {
    if (!searchTerm) return sortedCountries;
    return sortedCountries.filter(country =>
      country.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sortedCountries, searchTerm]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  // Handle country selection
  const handleCountrySelect = (country) => {
    const event = {
      target: {
        name,
        value: country
      }
    };
    onChange(event);
    setSearchTerm('');
    setIsOpen(false);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (searchable) {
      setIsOpen(true);
    }
  };

  // Handle input blur (with delay to allow clicking on options)
  const handleInputBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      setSearchTerm('');
    }, 150);
  };

  // Handle standard select change (fallback for non-searchable)
  const handleSelectChange = (e) => {
    onChange(e);
  };

  // If not searchable, use standard select
  if (!searchable) {
    return (
      <div className="country-selector-container">
        {label && (
          <label htmlFor={id || name} className="country-selector-label">
            {label}
            {required && <span className="required-indicator">*</span>}
          </label>
        )}
        
        <select
          id={id || name}
          name={name}
          value={value || ""}
          onChange={handleSelectChange}
          className={`country-selector ${className || ""} ${error ? 'has-error' : ''}`}
          disabled={disabled}
          required={required}
        >
          <option value="">{placeholder}</option>
          {sortedCountries.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
        
        {error && <div className="country-selector-error">{error}</div>}
      </div>
    );
  }

  // Searchable version
  return (
    <div className="country-selector-container">
      {label && (
        <label htmlFor={id || name} className="country-selector-label">
          {label}
          {required && <span className="required-indicator">*</span>}
        </label>
      )}
      
      <div className="searchable-country-selector">
        <input
          id={id || name}
          name={name}
          type="text"
          value={searchTerm || value || ''}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={value ? value : placeholder}
          className={`country-search-input ${className || ""} ${error ? 'has-error' : ''}`}
          disabled={disabled}
          required={required}
          autoComplete="off"
        />
        
        {isOpen && filteredCountries.length > 0 && (
          <div className="country-dropdown">
            {filteredCountries.slice(0, 10).map((country) => (
              <div
                key={country}
                className={`country-option ${value === country ? 'selected' : ''}`}
                onClick={() => handleCountrySelect(country)}
              >
                {country}
              </div>
            ))}
            {filteredCountries.length > 10 && (
              <div className="country-option-more">
                {filteredCountries.length - 10} more countries...
              </div>
            )}
          </div>
        )}
        
        {value && (
          <button
            type="button"
            className="clear-country-btn"
            onClick={() => handleCountrySelect('')}
            disabled={disabled}
          >
            ×
          </button>
        )}
      </div>
      
      {error && <div className="country-selector-error">{error}</div>}
    </div>
  );
};

export default CountrySelector;
export { COUNTRIES };